import useCircleLeaders from '@/participants/hooks/useCircleLeaders'
import { ThreadContext } from '@/thread/contexts/ThreadContext'
import { useAuth } from '@/user/hooks/useAuth'
import useCurrentMember from '@/member/hooks/useCurrentMember'
import { ThreadProposalVoteFragment } from '@gql'
import { useOrgContext } from '@/org/contexts/OrgContext'
import { getProposalVoterCircleId } from '@rolebase/shared/helpers/getProposalVoterCircleId'
import { resolveProposal } from '@rolebase/shared/helpers/resolveProposal'
import { ThreadActivityProposalFragment } from '@rolebase/shared/model/thread_activity'
import { useContext, useMemo } from 'react'
import useProposalVoters from './useProposalVoters'

export default function useProposalState(
  activity: ThreadActivityProposalFragment,
  votes?: ThreadProposalVoteFragment[]
) {
  const { user } = useAuth()
  const currentMember = useCurrentMember()
  const { circle } = useContext(ThreadContext)!
  const { orgData } = useOrgContext()
  // Everyone the rule designates, accounts or not — used only to say who cannot
  // vote (ProposalNonVotersNotice).
  const voters = useProposalVoters(activity.data.votersScope)

  // Those who can actually vote. The server counts exactly these
  // (loadProposal filters on userId), so counting the others here would make
  // this screen preview an outcome the server never applies — an absolute
  // majority out of 3 where the server computes out of 2.
  const participants = useMemo(
    () => voters.filter((voter) => !!voter.member.userId),
    [voters]
  )
  const leaders = useCircleLeaders(circle?.id)

  const inProgress = activity.data.status === 'inProgress'

  // Current user's vote
  const userVote = useMemo(
    () => user && votes?.find((v) => v.userId === user.id),
    [user?.id, votes]
  )

  // Result preview from the current votes
  const result = useMemo(
    () =>
      resolveProposal(
        activity.data.decisionMode,
        votes || [],
        participants.length
      ),
    [activity.data.decisionMode, votes, participants.length]
  )

  // A meeting decision is recorded by the circle, not by the author: the same
  // people who may amend the proposal may say what the room decided (D6, B4).
  // Mirrors the backend's meetingDeciderUserIds — the two must agree, or the
  // screen offers a button the server refuses.
  const canDecideInMeeting = useMemo(() => {
    if (activity.data.decisionMode !== 'meeting') return false
    if (!orgData || !currentMember || !circle) return false
    const ownerCircleId = getProposalVoterCircleId(orgData, circle.id)
    return [
      ...orgData.getParticipants(circle.id),
      ...orgData.getParticipants(ownerCircleId),
    ].some((p) => p.member.id === currentMember.id)
  }, [activity.data.decisionMode, orgData, currentMember, circle])

  // Author or a circle leader can resolve manually at any time
  const isAuthor = activity.userId === user?.id
  const isLeader = leaders.some((p) => p.member.id === currentMember?.id)
  const canResolve = inProgress && (isAuthor || isLeader || canDecideInMeeting)

  // Only the voters (participants of the thread or the role) can vote
  const isVoter = participants.some((p) => p.member.id === currentMember?.id)

  return {
    inProgress,
    userVote,
    result,
    canResolve,
    canDecideInMeeting,
    isAuthor,
    isVoter,
    votersCount: participants.length,
    voters,
  }
}
